import React, { useCallback } from 'react';
import { Form, Field } from 'react-final-form';

// material-ui
import {
  Grid,
  Card,
  CardContent,
  CardActions,
  Divider,
  Hidden,
  Box,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { VpnKey } from '@material-ui/icons';

// components
import TextField from 'components/TextField';
import Button from 'components/Button';
import Header from 'components/Header';

// api
import { useUpdateReportingChapterMutation } from 'api/reportingChapters';

// selectors
import { useSelector } from 'react-redux';
import getIsSystemAdmin from 'store/selectors/getIsSystemAdmin';

interface ReportingChapterFormProps {
  initialName?: string;
}

export default function ReportingChapterForm({
  initialName = '',
}: ReportingChapterFormProps) {
  const classes = useStyles();
  const {
    mutate: updateReportingChapter,
    isLoading,
    isSuccess,
    isError,
  } = useUpdateReportingChapterMutation();
  const isSystemAdmin = useSelector(getIsSystemAdmin);

  const handleSubmit = useCallback(
    (values: { name: string }, form: any) => {
      const trimmedName = values.name.trim();
      updateReportingChapter(
        { name: trimmedName },
        { onSuccess: () => form.restart({ name: trimmedName }) }
      );
    },
    [updateReportingChapter]
  );

  return (
    <Form
      onSubmit={handleSubmit}
      initialValues={{ name: initialName }}
      render={({ handleSubmit, invalid, pristine, form: { reset } }) => (
        <Card component='form' onSubmit={handleSubmit}>
          <CardContent>
            <Grid container>
              <Grid item xs={12}>
                <Header
                  icon={VpnKey}
                  title='Reporting Chapter'
                  subtitle='Manage the reporting chapter name'
                />
              </Grid>
              <Grid item xs>
                <Grid container spacing={2}>
                  <Hidden xsDown>
                    <Grid item>
                      <Box width={75} />
                    </Grid>
                  </Hidden>
                  <Grid item xs>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <Field
                          name='name'
                          component={TextField}
                          label='Reporting Chapter Name'
                          disabled={!isSystemAdmin}
                        />
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </CardContent>
          <Divider />
          {isSystemAdmin && (
            <CardActions className={classes.cardActions}>
              <Grid
                container
                justifyContent='flex-end'
                alignItems='center'
                spacing={1}
              >
                <Grid item>
                  <Button disabled={pristine || isLoading} onClick={reset}>
                    Cancel
                  </Button>
                </Grid>
                <Grid item>
                  <Button
                    type='submit'
                    color='primary'
                    variant='contained'
                    disabled={pristine || invalid || isLoading}
                    loading={isLoading}
                    success={isSuccess}
                    fail={isError}
                  >
                    Save
                  </Button>
                </Grid>
                <Grid item xs={1} />
              </Grid>
            </CardActions>
          )}
        </Card>
      )}
    />
  );
}

const useStyles = makeStyles({
  cardActions: { margin: 16 },
});
